// -----------------------------------------------------------------------------
// A8 NOTIFICATIONS (v0.9.0 — boards round 30 #10, round 31 #5, Kyle 30/Oct – 01/Nov/26). Opened from the profile menu
// (K avatar › Notifications; the badge on K counts what is new). Three tabs:
//   Pending action   requests I may decide: Client name / Project owner (super admin, Administrators) and prefix
//                    renames (also GM, Project head). Approve saves at once; Reject asks for an optional reason.
//   Pending request  my own requests still waiting.
//   Closed           every decided request, mine and the ones I decided: Approved / Rejected (+ reason) / Replaced.
// Opening the page marks decisions on my requests as seen (the badge drops). Data: notifications, request_decide,
// notifications_seen (0022). Bell only for now — no emails.
// -----------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { rpc } from "../api/backend";
import { useRpc } from "../api/useRpc";
import type { RequestRow } from "../api/types";
import { goBack, href, parseHash } from "../router";
import { formatDate } from "../format";
import { refreshBadge } from "../notifications";
import { CodeChip, ErrorBox, InfoIcon, Loading, Modal, toast } from "../ui/kit";
import { Icon } from "../ui/icons";

type Tab = "action" | "request" | "closed";
const KIND = { client: "Client name", owner: "Project owner", prefix: "Prefix rename" } as const;
const PILL = { approved: "p-closed", rejected: "p-crit", replaced: "p-void-plain", pending: "p-open" } as const;
const TIP = ["Changes that need approval, from every project you can approve on.",
  "Pending action: waiting for your Approve / Reject. Client name and Project owner: super admin and Administrators; prefix rename: also GM and Project head.",
  "Pending request: your own requests still waiting.",
  "Closed: every decided request, yours and the ones you decided — Approved, Rejected (with the reason) or Replaced (a newer request for the same field).",
  "The number on Closed counts decisions on your requests you have not opened yet (orange dot on the row); it clears when you open the Closed tab.",
  "The badge on your K avatar and on Notifications in the profile menu adds up Pending action and Closed.",
  "No emails for now."];

const change = (r: RequestRow) => (
  <><span className="muted">{r.old || "–"}</span> <Icon name="arrow_forward" /> <b>{r.new}</b>{r.kind === "prefix" && r.issues ? <> · {r.issues} issues re-coded</> : null}</>
);

export function NotificationsPage({ tab }: { tab: Tab }) {
  const { data, error, reload } = useRpc("notifications", {});
  const [rejecting, setRejecting] = useState<RequestRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // v0.9.1 (round 32 #7): decisions on my requests count on the Closed tab (orange) until I open that tab; the rows keep
  // their orange dot for this visit, so I can see which ones were new
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (tab !== "closed" || !data) return;
    const fresh = data.closed.filter((r) => r.unseen).map((r) => r.id);
    if (!fresh.length) return;
    setNewIds((cur) => new Set([...cur, ...fresh]));
    rpc("notifications_seen").then(() => { refreshBadge(); reload(); }).catch(() => {});
  }, [data, tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (r: RequestRow, approve: boolean, note = "") => {
    setBusy(r.id);
    try {
      await rpc("request_decide", { kind: r.kind === "prefix" ? "prefix" : "change", id: r.id, approve, note });
      toast(approve ? `Approved: ${KIND[r.kind]} ${r.code}` : `Rejected: ${KIND[r.kind]} ${r.code}`);
      setRejecting(null); refreshBadge(); reload();
    } catch (e) { toast((e as Error).message); }
    setBusy(null);
  };
  const unseen = data?.closed.filter((r) => r.unseen).length ?? 0;
  const tabs: [Tab, string, number | null][] = [["action", "Pending action", data?.pending_action.length ?? null],
    ["request", "Pending request", data?.pending_request.length ?? null], ["closed", "Closed", tab === "closed" ? null : unseen]];
  return (
    <div className="page notifications-page">
      <header className="page-header admin-head">
        <div className="crumb-row"><p className="eyebrow">Notifications</p>
          <div className="actions"><button className="btn icon close-x" aria-label="Close" onClick={() => goBack(parseHash(window.location.hash))}><Icon name="close" /></button></div></div>
        <div className="title-row"><h1 className="row gap8">Notifications <InfoIcon tip={TIP} /></h1></div>
        <nav className="tabs" aria-label="Notifications">
          {tabs.map(([t, name, n]) => {
            const count = n ? <span className={`tab-count${t === "request" ? "" : " hot"}`} data-testid={`count-${t}`}>{n}</span> : null;
            return t === tab
              ? <span key={t} className="tab on" aria-current="page">{name}{count}</span>
              : <a key={t} className="tab" href={href({ name: "notifications", tab: t })}>{name}{count}</a>;
          })}
        </nav>
      </header>
      {error && <ErrorBox message={error} />}
      {!data && !error && <Loading />}
      {data && tab !== "closed" && (
        <div className="req-list" data-testid={`list-${tab}`}>
          {(tab === "action" ? data.pending_action : data.pending_request).map((r) => (
            <div key={r.id} className="req-card" data-request={`${r.code}:${r.kind}`}>
              <div className="req-what"><CodeChip code={r.code} /><b>{KIND[r.kind]}</b></div>
              <div className="req-change">{change(r)}{r.reason && <p className="muted small">“{r.reason}”</p>}</div>
              <div className="req-who muted small">{r.by}<br />{formatDate(r.on)}</div>
              {tab === "action"
                ? <div className="req-act"><button className="btn" disabled={busy === r.id} onClick={() => setRejecting(r)}>Reject</button>
                    <button className="btn primary" disabled={busy === r.id} onClick={() => decide(r, true)}>Approve</button></div>
                : <div className="req-act"><span className="pill p-open">Waiting</span></div>}
            </div>
          ))}
          {(tab === "action" ? data.pending_action : data.pending_request).length === 0 &&
            <p className="muted pad" data-testid="nothing">{tab === "action" ? "Nothing waits for your approval." : "None of your requests is waiting."}</p>}
        </div>
      )}
      {data && tab === "closed" && (
        <div className="card table-card">
          <table className="closed-table" data-testid="list-closed">
            <colgroup><col style={{ width: 84 }} /><col style={{ width: 120 }} /><col /><col style={{ width: 120 }} /><col style={{ width: 90 }} /><col style={{ width: 96 }} /><col style={{ width: 230 }} /></colgroup>
            <thead><tr><th>Project</th><th>Change</th><th>From <Icon name="arrow_forward" /> to</th><th>Sent by</th><th>Sent</th><th>Result</th><th>By / note</th></tr></thead>
            <tbody>
              {data.closed.map((r) => (
                <tr key={r.id} className={r.unseen || newIds.has(r.id) ? "unseen" : ""} data-closed={`${r.code}:${r.kind}:${r.status}`}>
                  <td>{(r.unseen || newIds.has(r.id)) && <i className="new-dot" title="New" data-testid="new-dot" />}<CodeChip code={r.code} /></td><td>{KIND[r.kind]}</td><td className="ellipsis">{change(r)}</td><td>{r.by}</td><td>{formatDate(r.on)}</td>
                  <td><span className={`pill ${PILL[r.status]}`}>{r.status[0].toUpperCase() + r.status.slice(1)}</span></td>
                  <td className="ellipsis muted">{r.status === "replaced" ? "a newer request" : r.decided_by}{r.note && <> · “{r.note}”</>}</td>
                </tr>
              ))}
              {data.closed.length === 0 && <tr><td colSpan={7} className="muted">No decided requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {rejecting && <RejectModal row={rejecting} busy={busy === rejecting.id} onClose={() => setRejecting(null)} onReject={(note) => decide(rejecting, false, note)} />}
    </div>
  );
}

function RejectModal({ row, busy, onClose, onReject }: { row: RequestRow; busy: boolean; onClose: () => void; onReject: (note: string) => void }) {
  const [note, setNote] = useState("");
  return (
    <Modal title={`Reject · ${KIND[row.kind]} ${row.code}`} onClose={onClose} className="small-wide">
      <p className="req-change">{change(row)}</p>
      <label className="field"><span>Reason <span className="faint">(optional — {row.by} sees it)</span></span>
        <textarea autoFocus value={note} onChange={(e) => setNote(e.target.value)} aria-label="Reason" /></label>
      <div className="modal-foot"><button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn danger" disabled={busy} onClick={() => onReject(note.trim())}>Reject</button></div>
    </Modal>
  );
}
